import { api, type User } from './api';

export type Todo = {
  id: number;
  title: string;
  description: string | null;
  status: 'open' | 'done';
  createdBy: User;
  assignedTo: User | null;
  dueDate: string | null;
  overdue: boolean;
  completedAt: string | null;
  recurringRuleId: number | null;
  createdAt: string;
  updatedAt: string;
};

export type RecurringRule = {
  id: number;
  title: string;
  description: string | null;
  createdBy: User;
  assignedTo: User | null;
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;
  weekdays: number[] | null;
  nextRunDate: string;
  lastGeneratedDate: string | null;
  active: boolean;
};

export type TodoFilter = {
  status?: 'open' | 'done' | 'all';
  assigned?: 'all' | 'me' | 'them' | 'none';
};

export const todos = {
  list: (filter: TodoFilter = {}) => {
    const q = new URLSearchParams();
    if (filter.status) q.set('status', filter.status);
    if (filter.assigned) q.set('assigned', filter.assigned);
    const suffix = q.size ? `?${q}` : '';
    return api.get<{ todos: Todo[] }>(`/api/todos${suffix}`);
  },

  add: (body: {
    title: string;
    description?: string | null;
    assignedToUserId?: number | null;
    dueDate?: string | null;
  }) => api.post<{ id: number }>('/api/todos', body),

  update: (
    id: number,
    patch: {
      title?: string;
      description?: string | null;
      assignedToUserId?: number | null;
      dueDate?: string | null;
      status?: 'open' | 'done';
    },
  ) => api.patch(`/api/todos/${id}`, patch),

  remove: (id: number) => api.del(`/api/todos/${id}`),

  rules: () => api.get<{ rules: RecurringRule[] }>('/api/todos/recurring'),

  addRule: (body: {
    title: string;
    description?: string | null;
    assignedToUserId?: number | null;
    frequency: RecurringRule['frequency'];
    interval?: number;
    weekdays?: number[];
    startsOn?: string;
  }) => api.post<{ id: number }>('/api/todos/recurring', body),

  updateRule: (
    id: number,
    patch: {
      title?: string;
      description?: string | null;
      assignedToUserId?: number | null;
      frequency?: RecurringRule['frequency'];
      interval?: number;
      weekdays?: number[] | null;
      nextRunDate?: string;
      active?: boolean;
    },
  ) => api.patch(`/api/todos/recurring/${id}`, patch),

  removeRule: (id: number) => api.del(`/api/todos/recurring/${id}`),
};

export const WEEKDAYS = [
  { id: 0, label: 'Sun' },
  { id: 1, label: 'Mon' },
  { id: 2, label: 'Tue' },
  { id: 3, label: 'Wed' },
  { id: 4, label: 'Thu' },
  { id: 5, label: 'Fri' },
  { id: 6, label: 'Sat' },
] as const;

export function ruleSummary(rule: RecurringRule): string {
  const n = rule.interval;
  const unit =
    rule.frequency === 'daily' ? (n === 1 ? 'day' : 'days')
    : rule.frequency === 'weekly' ? (n === 1 ? 'week' : 'weeks')
    : n === 1 ? 'month' : 'months';
  const every = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}`;
  if (rule.frequency === 'weekly' && rule.weekdays?.length) {
    const names = rule.weekdays.map((d) => WEEKDAYS[d]?.label ?? d).join(', ');
    return `${every} on ${names}`;
  }
  return every;
}
